import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isSuperAdmin, ROLE_LABELS } from "../lib/states";
import {
  Activity, Users, TrendingUp, Download, Phone, MessageSquare,
  ArrowRightCircle, UserCheck, Flame, PlusCircle, Loader2, Handshake, LogIn,
} from "lucide-react";

const WINDOWS = [
  { key: "24h", label: "Last 24h" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
];

// Ultra-compact relative time helper (used only on this page).
function relative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const mins = (Date.now() - then) / 60000;
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.floor(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.floor(hrs)}h ago`;
  const days = hrs / 24;
  if (days <= 30) return `${Math.floor(days)}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}
function fullTs(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const KIND_ICON = {
  call: Phone, chat: MessageSquare, state_change: ArrowRightCircle,
  assigned: UserCheck, hot: Flame, created: PlusCircle, lead_pull: Download,
  activity: Handshake, action: Handshake, login: LogIn,
};

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-stone-900/95 border border-stone-800 rounded-xl p-5 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-stone-400 text-xs uppercase tracking-wider">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="text-3xl font-semibold text-white tabular-nums">{value}</div>
      {sub && <p className="text-xs text-stone-500">{sub}</p>}
    </div>
  );
}

function PersonRow({ p }) {
  const cells = [
    { label: "Total", v: p.total, cls: "text-white font-semibold" },
    { label: "Calls", v: p.calls, cls: p.calls ? "text-emerald-400" : "text-stone-600" },
    { label: "Chats", v: p.chats, cls: p.chats ? "text-emerald-400" : "text-stone-600" },
    { label: "Moves", v: p.stage_moves, cls: p.stage_moves ? "text-sky-400" : "text-stone-600" },
    { label: "Assign", v: p.assignments, cls: p.assignments ? "text-violet-400" : "text-stone-600" },
    { label: "Hot", v: p.hot_flags, cls: p.hot_flags ? "text-red-400" : "text-stone-600" },
    { label: "Added", v: p.leads_added, cls: p.leads_added ? "text-amber-400" : "text-stone-600" },
    { label: "Notes", v: p.notes_or_activity, cls: p.notes_or_activity ? "text-stone-200" : "text-stone-600" },
  ];
  return (
    <div data-testid={`person-row-${p.email}`}
      className="grid grid-cols-[minmax(0,1.4fr)_repeat(8,minmax(0,1fr))] gap-2 items-center px-3 py-2 rounded-lg hover:bg-stone-900/60 transition-colors">
      <div className="min-w-0">
        <p className="text-sm text-stone-100 font-medium truncate">{p.name}</p>
        <p className="text-[10px] text-stone-500 uppercase tracking-wider">{ROLE_LABELS[p.role] || p.role}</p>
      </div>
      {cells.map((c) => (
        <div key={c.label} className="text-center">
          <div className={`text-sm tabular-nums ${c.cls}`}>{c.v}</div>
        </div>
      ))}
    </div>
  );
}

function TrailRow({ e }) {
  const Icon = KIND_ICON[e.kind] || Handshake;
  return (
    <div data-testid={`trail-row`} className="flex items-start gap-3 px-3 py-2.5 border-l border-stone-800 hover:bg-stone-900/60 transition-colors">
      <div className="w-8 h-8 rounded-full bg-stone-800 border border-stone-700 flex items-center justify-center shrink-0">
        <Icon className="h-3.5 w-3.5 text-stone-300" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-stone-100 leading-snug">
          <span className="font-medium">{e.actor}</span>
          <span className="text-stone-400"> · </span>
          <span className="text-stone-300">{e.detail}</span>
        </p>
        <p className="text-[10px] text-stone-500" title={fullTs(e.ts)}>{relative(e.ts)}</p>
      </div>
    </div>
  );
}

export default function ActivityMonitor() {
  const { user } = useAuth();
  const [win, setWin] = useState("24h");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSuperAdmin(user)) return;
    setLoading(true);
    api.get(`/activity-monitor?window=${win}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Load failed"))
      .finally(() => setLoading(false));
  }, [user, win]);

  const activeCard = useMemo(() => data?.per_person?.length || 0, [data]);

  if (!isSuperAdmin(user)) {
    return <div className="p-6 text-sm text-stone-500">Not available for your role.</div>;
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-black text-stone-100 overflow-hidden">
      <header className="px-6 pt-6 pb-4 border-b border-stone-900 shrink-0">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Activity className="h-5 w-5 text-emerald-400" />
            <div className="min-w-0">
              <h1 className="font-heading text-xl tracking-tight text-white">Activity Monitor</h1>
              <p className="text-xs text-stone-500">
                Team usage &amp; audit trail — visible to Super Admin only.
                {data?.last_wati_pull_at && <> · Last WATI pull {relative(data.last_wati_pull_at)}.</>}
              </p>
            </div>
          </div>
          <div className="inline-flex bg-stone-900 border border-stone-800 rounded-md p-0.5" data-testid="window-tabs">
            {WINDOWS.map((w) => (
              <button key={w.key} onClick={() => setWin(w.key)}
                data-testid={`window-${w.key}`}
                className={`text-xs font-medium px-3 py-1.5 rounded transition-colors ${
                  win === w.key ? "bg-stone-100 text-stone-900" : "text-stone-400 hover:text-stone-100"}`}>
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto px-6 py-5 space-y-5">
        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Active users" value={loading ? "…" : activeCard}
            sub={`Reported at least one action in ${WINDOWS.find(w=>w.key===win).label.toLowerCase()}`}
            testid="stat-active-users" />
          <StatCard icon={TrendingUp} label="Total actions" value={loading ? "…" : (data?.total_actions ?? 0)}
            sub="Calls · chats · stage moves · notes · assignments"
            testid="stat-total-actions" />
          <StatCard icon={Download} label="Last WATI pull"
            value={data?.last_wati_pull_at ? relative(data.last_wati_pull_at) : "—"}
            sub={data?.last_wati_pull_at ? fullTs(data.last_wati_pull_at) : "No pull recorded yet"}
            testid="stat-last-pull" />
        </div>

        {/* Per-person table */}
        <section data-testid="per-person-section" className="bg-stone-950 border border-stone-900 rounded-xl">
          <div className="px-4 py-3 border-b border-stone-900 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-stone-100">Who did what</h2>
            <span className="text-[11px] text-stone-500">{data?.per_person?.length || 0} user(s)</span>
          </div>
          {/* Column header */}
          <div className="grid grid-cols-[minmax(0,1.4fr)_repeat(8,minmax(0,1fr))] gap-2 items-center px-3 py-2 text-[10px] uppercase tracking-wider text-stone-500 border-b border-stone-900">
            <span>User</span>
            {["Total","Calls","Chats","Moves","Assign","Hot","Added","Notes"].map((h) =>
              <span key={h} className="text-center">{h}</span>)}
          </div>
          <div className="divide-y divide-stone-900">
            {loading ? (
              <div className="p-8 text-center text-stone-500 text-sm flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : (data?.per_person || []).length === 0 ? (
              <p data-testid="per-person-empty" className="p-6 text-sm text-stone-500 text-center">No user activity in this window.</p>
            ) : (
              data.per_person.map((p) => <PersonRow key={p.email} p={p} />)
            )}
          </div>
        </section>

        {/* Trail */}
        <section data-testid="trail-section" className="bg-stone-950 border border-stone-900 rounded-xl">
          <div className="px-4 py-3 border-b border-stone-900 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-stone-100">Latest activity</h2>
            <span className="text-[11px] text-stone-500">Last {data?.trail?.length || 0} events</span>
          </div>
          <div className="max-h-[520px] overflow-auto">
            {loading ? (
              <div className="p-8 text-center text-stone-500 text-sm flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : (data?.trail || []).length === 0 ? (
              <p data-testid="trail-empty" className="p-6 text-sm text-stone-500 text-center">No events in this window.</p>
            ) : (
              <div className="divide-y divide-stone-900">
                {data.trail.map((e, i) => <TrailRow key={i} e={e} />)}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
